import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Send, 
  Search, 
  Plus,
  Star,
  User,
  Users
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

interface Conversation {
  id: string;
  participantName: string;
  participantRole: 'instructor' | 'support';
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  courseName?: string;
}

const StudentMessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');

  const [conversations] = useState<Conversation[]>([
    {
      id: '1',
      participantName: 'Marie Dubois',
      participantRole: 'instructor',
      lastMessage: 'Parfait ! N\'hésitez pas si vous avez d\'autres questions.',
      lastMessageTime: '2024-01-27T14:30:00',
      unreadCount: 0,
      courseName: 'JavaScript pour débutants'
    },
    {
      id: '2',
      participantName: 'Support Technique',
      participantRole: 'support',
      lastMessage: 'Nous avons résolu le problème. Votre accès est maintenant rétabli.',
      lastMessageTime: '2024-01-26T16:45:00',
      unreadCount: 2
    },
    {
      id: '3',
      participantName: 'Jean Martin',
      participantRole: 'instructor',
      lastMessage: 'Excellente progression ! Continuez comme ça.',
      lastMessageTime: '2024-01-25T11:20:00',
      unreadCount: 0,
      courseName: 'Python pour débutants'
    }
  ]);

  const roleConfig = {
    instructor: { label: 'Instructeur', color: 'bg-blue-500', icon: User },
    support: { label: 'Support', color: 'bg-green-500', icon: Users }
  };

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    setIsLoading(false);
  };



  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    
    toast({
      title: 'Message envoyé',
      description: 'Votre message a été envoyé avec succès.'
    });
    
    setNewMessage('');
  };

  const filteredConversations = conversations.filter(conv => 
    searchTerm === '' || 
    conv.participantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  const selectedConv = conversations.find(c => c.id === selectedConversation);
  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);



  return (
    <StudentLayout
      title="Messages"
      description={`Communiquez avec vos instructeurs et le support${totalUnread > 0 ? ` (${totalUnread} non lu${totalUnread > 1 ? 's' : ''})` : ''}`}
      onStudentProfileLoaded={handleStudentProfileLoaded}
      headerContent={
        <Button
          onClick={() => setSelectedConversation(null)}
          className="flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Nouveau message</span>
        </Button>
      }
    >
      <SEOMetaTags 
        title="Messages - E-Learning"
        description="Communiquez avec vos instructeurs et le support technique."
        keywords="messages, communication, instructeurs, support, e-learning"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Liste des conversations */}
            <div className="lg:col-span-1">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-0">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {filteredConversations.map((conversation, index) => {
                      const roleInfo = roleConfig[conversation.participantRole];
                      const RoleIcon = roleInfo.icon;
                      
                      return (
                        <motion.div
                          key={conversation.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                            selectedConversation === conversation.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                          onClick={() => setSelectedConversation(conversation.id)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="relative">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback>
                                  {conversation.participantName.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${roleInfo.color} flex items-center justify-center`}>
                                <RoleIcon className="h-2 w-2 text-white" />
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                  {conversation.participantName}
                                </h3>
                                <span className="text-xs text-gray-500">
                                  {formatTime(conversation.lastMessageTime)}
                                </span>
                              </div>
                              
                              {conversation.courseName && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                                  {conversation.courseName}
                                </p>
                              )}
                              
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {conversation.lastMessage}
                                </p>
                                {conversation.unreadCount > 0 && (
                                  <Badge className="bg-blue-500 text-white ml-2">
                                    {conversation.unreadCount}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Zone de conversation */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg h-96">
                <CardContent className="p-0 h-full flex flex-col">
                  {selectedConv ? (
                    <>
                      {/* Header */}
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback>
                              {selectedConv.participantName.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h2 className="font-semibold text-gray-900 dark:text-white">
                              {selectedConv.participantName}
                            </h2>
                            <div className="flex items-center space-x-2">
                              <Badge className={`${roleConfig[selectedConv.participantRole].color} text-white`}>
                                {roleConfig[selectedConv.participantRole].label}
                              </Badge>
                              {selectedConv.courseName && (
                                <span className="text-sm text-gray-500">
                                  {selectedConv.courseName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Messages */}
                      <div className="flex-1 p-4 overflow-y-auto">
                        <div className="space-y-4">
                          <div className="flex justify-start">
                            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg max-w-xs">
                              <p className="text-sm text-gray-900 dark:text-white">
                                {selectedConv.lastMessage}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatTime(selectedConv.lastMessageTime)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Zone de saisie */}
                      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex space-x-2">
                          <Textarea
                            placeholder="Tapez votre message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            className="flex-1 min-h-[60px] resize-none"
                          />
                          <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          Sélectionnez une conversation
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          Choisissez une conversation pour commencer à échanger
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
    </StudentLayout>
  );
};

export default StudentMessagesPage;
